import React, { useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { Alert, Card, CardBody, Col, Container, FormFeedback, Input, Label, Row } from "reactstrap"
import logoDark from "../../../assets/images/pdflogo.png"
import logoLight from "../../../assets/images/pdflogo.png"
import * as Yup from "yup"
import { useFormik } from "formik"
import { resetPasswordApi } from "helpers/auth_api"

const ResetPasswordPage = () => {
  document.title = "Reset Password | Lexa - Responsive Bootstrap 5 Admin Dashboard"

  const [searchParams] = useSearchParams()
  const tokenFromUrl = searchParams.get("token") || ""

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      newPassword: Yup.string()
        .required("Please enter new password")
        .min(6, "Password must be at least 6 characters"),
      confirmPassword: Yup.string()
        .required("Please confirm password")
        .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
    }),
    onSubmit: async (values: { newPassword: string; confirmPassword: string }) => {
      if (!tokenFromUrl) {
        setError("Invalid or missing reset link. Please request a new one.")
        return
      }
      setError(null)
      setLoading(true)
      try {
        const res = await resetPasswordApi({
          token: tokenFromUrl,
          newPassword: values.newPassword,
        })
        if (res.success) {
          setSuccess(true)
        } else {
          setError(res.message || "Failed to reset password.")
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.message || err?.message || "Failed to reset password."
        setError(msg)
      } finally {
        setLoading(false)
      }
    },
  })

  if (!tokenFromUrl && !success) {
    return (
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <CardBody className="pt-0">
                  <h3 className="text-center mt-5 mb-4">
                    <Link to="/" className="d-block auth-logo">
                      <img src={logoDark} alt="" height="30" className="auth-logo-dark" />
                      <img src={logoLight} alt="" height="30" className="auth-logo-light" />
                    </Link>
                  </h3>
                  <div className="p-3">
                    <h4 className="text-muted font-size-18 mb-3 text-center">Reset Password</h4>
                    <Alert color="warning">
                      Invalid or missing reset link. Please use the link from your email or{" "}
                      <Link to="/forgot-password">request a new one</Link>.
                    </Alert>
                    <div className="mt-4 text-center">
                      <Link to="/login" className="btn btn-primary">
                        Back to Sign In
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  <Link to="/" className="btn btn-outline-primary btn-sm">
                    <i className="mdi mdi-home me-1"></i>Go to Home
                  </Link>
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  if (success) {
    return (
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <CardBody className="pt-0">
                  <h3 className="text-center mt-5 mb-4">
                    <Link to="/" className="d-block auth-logo">
                      <img src={logoDark} alt="" height="30" className="auth-logo-dark" />
                      <img src={logoLight} alt="" height="30" className="auth-logo-light" />
                    </Link>
                  </h3>
                  <div className="p-3">
                    <h4 className="text-muted font-size-18 mb-3 text-center">Password Reset</h4>
                    <Alert color="success">
                      Your password has been reset successfully. You can now sign in with your new
                      password.
                    </Alert>
                    <div className="mt-4 text-center">
                      <Link to="/login" className="btn btn-primary">
                        Sign In
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  <Link to="/login" className="text-primary">Sign In</Link>
                </p>
                <p>
                  <Link to="/" className="btn btn-outline-primary btn-sm">
                    <i className="mdi mdi-home me-1"></i>Go to Home
                  </Link>
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <div className="account-pages my-5 pt-sm-5">
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6} xl={5}>
            <Card className="overflow-hidden">
              <CardBody className="pt-0">
                <h3 className="text-center mt-5 mb-4">
                  <Link to="/" className="d-block auth-logo">
                    <img src={logoDark} alt="" height="30" className="auth-logo-dark" />
                    <img src={logoLight} alt="" height="30" className="auth-logo-light" />
                  </Link>
                </h3>
                <div className="p-3">
                  <h4 className="text-muted font-size-18 mb-3 text-center">Set New Password</h4>
                  {error ? (
                    <Alert color="danger" style={{ marginTop: "13px" }}>
                      {error}
                    </Alert>
                  ) : null}
                  <form
                    className="form-horizontal mt-4"
                    onSubmit={(e) => {
                      e.preventDefault()
                      validation.handleSubmit()
                      return false
                    }}
                  >
                    <div className="mb-3">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        name="newPassword"
                        type="password"
                        className="form-control"
                        placeholder="Enter new password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.newPassword}
                        invalid={
                          validation.touched.newPassword && !!validation.errors.newPassword
                        }
                      />
                      {validation.touched.newPassword && validation.errors.newPassword ? (
                        <FormFeedback type="invalid">
                          {validation.errors.newPassword}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <div className="mb-3">
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <Input
                        name="confirmPassword"
                        type="password"
                        className="form-control"
                        placeholder="Confirm new password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.confirmPassword}
                        invalid={
                          validation.touched.confirmPassword &&
                          !!validation.errors.confirmPassword
                        }
                      />
                      {validation.touched.confirmPassword &&
                      validation.errors.confirmPassword ? (
                        <FormFeedback type="invalid">
                          {validation.errors.confirmPassword}
                        </FormFeedback>
                      ) : null}
                    </div>
                    <Row className="mb-3">
                      <div className="col-12 text-end">
                        <button
                          className="btn btn-primary w-md waves-effect waves-light"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? "Resetting…" : "Reset Password"}
                        </button>
                      </div>
                    </Row>
                  </form>
                </div>
              </CardBody>
            </Card>
            <div className="mt-5 text-center">
              <p>
                Remember it? <Link to="/login" className="text-primary">Sign In Here</Link>
              </p>
              <p>
                <Link to="/" className="btn btn-outline-primary btn-sm">
                  <i className="mdi mdi-home me-1"></i>Go to Home
                </Link>
              </p>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ResetPasswordPage
